/**
 * OrderLedgerBuilder.js - REWRITTEN based on order_ledger.py
 *
 * Uses FIFO inventory tracking with qty_open to handle:
 * - Partial fills
 * - Multiple entries/exits
 * - Position pyramiding
 * - Mixed long/short positions
 */

console.log("ðŸš€ OrderLedgerBuilder.js v3.0 LOADED - FIFO Inventory System");

class OrderLedgerBuilder {
  constructor() {
    // Separate FIFO inventories for long and short positions per symbol
    this.longInventory = {}; // { symbol: [lot1, lot2, ...] }
    this.shortInventory = {}; // { symbol: [lot1, lot2, ...] }

    // Closed positions (realized)
    this.closedPositions = [];

    // NOTE:
    // Alpaca order history/streaming often emits multiple snapshots for the SAME order.id
    // as partial fills happen. We dedupe snapshots up-front in processOrderHistory().
    // Keeping this set for backward compatibility, but we do NOT use it to skip processing.
    this.processedOrderIds = new Set();
    this.bracketPlanById = new Map();
  }

  /**
   * Flatten bracket orders - extracts legs into separate orders
   * Matches Python's flatten_orders() behavior
   */
  flattenBracketOrders(orders) {
    const flattened = [];
    
    for (const order of orders) {
      // Always include the parent order
      flattened.push(order);
      
      // If this order has legs, extract them as separate orders
      const legs = order.legs;
      if (Array.isArray(legs) && legs.length > 0) {
        console.log(`   📎 Extracting ${legs.length} legs from order ${order.id}`);
        
        for (const leg of legs) {
          if (leg && typeof leg === 'object') {
            // Create a copy and link back to parent
            const legOrder = { ...leg };
            legOrder._parent_id = order.id;
            legOrder.symbol = order.symbol;  // Inherit symbol
            legOrder.asset_class = order.asset_class;  // Inherit asset_class
            flattened.push(legOrder);
            
            console.log(`      → Leg: ${leg.type} ${leg.side} limit=${leg.limit_price} stop=${leg.stop_price}`);
          }
        }
      }
    }
    
    return flattened;
  }

  /**
   * Main entry point - processes full order array
   */
  processOrderHistory(allOrders) {
    console.log(
      "ðŸ—‚ï¸ OrderLedgerBuilder.processOrderHistory called with:",
      allOrders?.length,
      "orders"
    );

    // Reset state
    this.longInventory = {};
    this.shortInventory = {};
    this.closedPositions = [];
    this.processedOrderIds.clear();

    if (!allOrders || allOrders.length === 0) {
      console.log("âš ï¸ No orders to process");
      return this.getTableView();
    }

    try {
      // STEP 0: Unwrap order objects from event wrappers
      console.log("ðŸ“¦ Step 0 - Unwrapping orders...");
      const unwrappedOrders = allOrders.map((item) => item.order || item);
      console.log("ðŸ“¦ Unwrapped:", unwrappedOrders.length, "orders");

      // STEP 0.1: Flatten bracket orders (extract embedded legs)
      console.log("📦 Step 0.1 - Flattening bracket legs...");
      const flattenedOrders = this.flattenBracketOrders(unwrappedOrders);
      console.log("📦 Flattened:", flattenedOrders.length, "orders (extracted legs)");

      // Normalize asset_class so options don't get dropped
      const normalizeAssetClass = (o) => {
        const sym = o?.symbol || "";
        const ac = (o?.asset_class || "").toLowerCase();
        console.log("asset_class:", o.asset_class)

        // If Alpaca gives "option" instead of "us_option"
        if (ac === "option" || ac === "options") return "us_option";
        if (ac === "equity" || ac === "stock") return "us_equity";

        // If missing asset_class, infer from symbol format (e.g. SPY260206P00675000)
        const looksLikeOCC = /^[A-Z]{1,6}\d{6}[CP]\d{8}$/.test(sym);
        if (!ac && looksLikeOCC) return "us_option";

        // Default
        return o?.asset_class || "us_equity";
      };

      for (const o of flattenedOrders) {
        o.asset_class = normalizeAssetClass(o);
      }


      // STEP 0.5: Deduplicate multiple snapshots of the same order.id.
      // Prefer (a) larger filled_qty, then (b) later filled_at/updated_at/submitted_at.
      console.log("ðŸ“¦ Step 0.5 - Deduping order snapshots by id...");
      const byId = new Map();
      for (const o of flattenedOrders) {
        if (!o?.id) continue;

        const prev = byId.get(o.id);
        if (!prev) {
          byId.set(o.id, o);
          continue;
        }

        const prevFilled = parseFloat(prev.filled_qty || 0);
        const curFilled = parseFloat(o.filled_qty || 0);

        const prevT = new Date(
          prev.filled_at || prev.updated_at || prev.submitted_at || 0
        ).getTime();
        const curT = new Date(
          o.filled_at || o.updated_at || o.submitted_at || 0
        ).getTime();

        const pickCur =
          curFilled > prevFilled || (curFilled === prevFilled && curT > prevT);
        if (pickCur) byId.set(o.id, o);
      }

      const dedupedOrders = Array.from(byId.values());
      console.log("Note: Flattened orders may include parent bracket orders with legs");
      console.log("ðŸ“¦ Deduped:", dedupedOrders.length, "orders");
      console.log(
        "ðŸ“¦ Orders: raw=",
        allOrders?.length,
        "unwrapped=",
        unwrappedOrders.length,
        "deduped=",
        dedupedOrders.length
      );

      this.bracketPlanById = new Map();
      for (const o of dedupedOrders) {
        const id = o?.id;
        if (!id) continue;
        const [tp_limit, sl_stop, sl_limit] = this.extractBracketInfo(o);
        if (tp_limit != null || sl_stop != null || sl_limit != null) {
          console.log(`      ðŸ"Œ Storing bracket plan for order ${id}: TP=${tp_limit} SL_STOP=${sl_stop} SL_LMT=${sl_limit}`);
          this.bracketPlanById.set(id, { tp_limit, sl_stop, sl_limit });
        }
      }
      console.log(`      ðŸ—ºï¸ Total bracket plans stored: ${this.bracketPlanById.size}`);

      // STEP 1: Filter to only filled orders
      console.log("ðŸ“‹ Step 1 - Filtering filled orders...");
      // Match the Python ledger behavior: qty-only qualifies as filled.
      // (Price fields can be missing/0 on intermediate snapshots; we already deduped.)
      const filledOrders = dedupedOrders.filter(
        (order) => parseFloat(order.filled_qty || 0) > 0
      );
      console.log("ðŸ“‹ Filled orders:", filledOrders.length);
      
      // ðŸ” DEBUG: inspect option intent quality BEFORE processing
      const opt = filledOrders.filter(o => o.asset_class === "us_option");
      const intentCounts = opt.reduce((m, o) => {
        const k =
          String(o.position_intent || "")
            .toLowerCase()
            .replaceAll("-", "_")
            .trim() || "(missing)";
        m[k] = (m[k] || 0) + 1;
        return m;
      }, {});
      console.log("ðŸ§¾ OPTION intent counts:", intentCounts);

      // Count asset classes
      const equityCount = filledOrders.filter(
        (o) => o.asset_class === "us_equity"
      ).length;
      const optionCount = filledOrders.filter(
        (o) => o.asset_class === "us_option"
      ).length;
      console.log(`ðŸ“‹ Asset classes: ${equityCount} stocks, ${optionCount} options`);

      // STEP 2: Sort chronologically by filled_at (fallback to submitted_at)
      console.log("ðŸ“‹ Step 2 - Sorting chronologically...");
      const sortedOrders = [...filledOrders].sort((a, b) => {
        const timeA = new Date(a.filled_at || a.submitted_at || 0);
        const timeB = new Date(b.filled_at || b.submitted_at || 0);
        return timeA - timeB;
      });
      console.log("ðŸ“‹ Sorted:", sortedOrders.length, "orders");

      // STEP 3: Process each order with FIFO inventory
      console.log("ðŸ“‹ Step 3 - Processing orders with FIFO...");
      sortedOrders.forEach((order, idx) => {
        if (idx < 5) {
          console.log(
            `   [${idx}] ${order.symbol} ${order.side} ${order.filled_qty} @ ${order.filled_avg_price}`
          );
        }
        this.processOrder(order);
      });

      console.log("âœ… Processing complete");
      console.log("   - Closed positions:", this.closedPositions.length);
      console.log(
        "   - Active long positions:",
        Object.values(this.longInventory).flat().length
      );
      console.log(
        "   - Active short positions:",
        Object.values(this.shortInventory).flat().length
      );

      return this.getTableView();
    } catch (error) {
      console.error("ðŸ’¥ ERROR in processOrderHistory:", error);
      console.error("ðŸ’¥ Stack:", error.stack);
      return this.getTableView();
    }
  }

  /**
   * Process individual order using FIFO inventory
   */
  processOrder(order) {
    if (!order || !order.symbol) return;

    // IMPORTANT:
    // Do NOT skip by order.id here. Alpaca emits multiple snapshots per id.
    // We dedupe snapshots up-front in processOrderHistory().

    const symbol = order.symbol;
    const assetClass = order.asset_class;

    console.log(`   ðŸ” Processing: ${symbol} asset_class=${assetClass}`);

    if (assetClass === "us_equity") {
      this.processStockOrder(order, symbol);
    } else if (assetClass === "us_option") {
      this.processOptionOrder(order, symbol);
    } else {
      console.log(`   âš ï¸ UNKNOWN asset_class: ${assetClass}`);
    }
  }

  /**
   * Process stock order with FIFO inventory
   */
  processStockOrder(order, symbol) {
    const action = this.classifyAction(order);
    const qty = parseFloat(order.filled_qty || 0);
    const price = parseFloat(order.filled_avg_price || 0);
    const submittedAt = order.submitted_at;
    const filledAt = order.filled_at;

    console.log(`   ðŸ“Œ ${symbol} ${order.side} ${qty} @ ${price} â†’ ${action}`);

    // Initialize inventories if needed
    if (!this.longInventory[symbol]) this.longInventory[symbol] = [];
    if (!this.shortInventory[symbol]) this.shortInventory[symbol] = [];

    const longCount = this.longInventory[symbol].length;
    const shortCount = this.shortInventory[symbol].length;
    console.log(`      Inventory before: LONG=${longCount} lots, SHORT=${shortCount} lots`);

    if (action === "OPEN_LONG") {
      // Add to long inventory
      const lot = this.createLot(order, "LONG", qty, price, submittedAt, filledAt);
      this.longInventory[symbol].push(lot);
      console.log(`      âœ… Added LONG lot: qty_open=${lot.qty_open}`);
    } else if (action === "CLOSE_LONG") {
      // Close from long inventory using FIFO
      this.closeLongFIFO(symbol, qty, price, filledAt, order);
    } else if (action === "OPEN_SHORT") {
      // Add to short inventory
      const lot = this.createLot(order, "SHORT", qty, price, submittedAt, filledAt);
      this.shortInventory[symbol].push(lot);
      console.log(`      âœ… Added SHORT lot: qty_open=${lot.qty_open}`);
    } else if (action === "CLOSE_SHORT") {
      // Close from short inventory using FIFO
      this.closeShortFIFO(symbol, qty, price, filledAt, order);
    } else {
      console.log(`      âš ï¸ UNKNOWN action: ${action}`);
    }
  }

  /**
   * Process option order with FIFO inventory
   */
  processOptionOrder(order, symbol) {
    // Normalize intent (Alpaca sometimes omits it or uses different casing)
    let intent = String(order.position_intent || "")
      .toLowerCase()
      .replaceAll("-", "_")
      .trim();
  
    const side = String(order.side || "").toLowerCase().trim();
    const qty = parseFloat(order.filled_qty || 0);
  
    // Options sometimes have filled_avg_price as 0, use limit_price as fallback
    let price = parseFloat(order.filled_avg_price || 0);
    if (price === 0 && order.limit_price) {
      price = parseFloat(order.limit_price);
    }
  
    const submittedAt = order.submitted_at;
    const filledAt = order.filled_at;
  
    // Initialize inventories if needed
    if (!this.longInventory[symbol]) this.longInventory[symbol] = [];
    if (!this.shortInventory[symbol]) this.shortInventory[symbol] = [];
  
    // âœ… FALLBACK: infer intent if missing
    if (!intent) {
      const hasLongOpen = this.longInventory[symbol].some(l => (l.qty_open || 0) > 0);
      const hasShortOpen = this.shortInventory[symbol].some(l => (l.qty_open || 0) > 0);
  
      if (side === "buy") {
        intent = hasShortOpen ? "buy_to_close" : "buy_to_open";
      } else if (side === "sell") {
        intent = hasLongOpen ? "sell_to_close" : "sell_to_open";
      }
      console.log(`   ðŸ§© OPTION intent missing â†’ inferred intent=${intent} (side=${side}, hasLong=${hasLongOpen}, hasShort=${hasShortOpen})`);
    }
  
    console.log(`   ðŸ“Œ OPTION ${symbol} intent=${intent} qty=${qty} @ ${price}`);
  
    if (intent === "buy_to_open") {
      const lot = this.createLot(order, "LONG", qty, price, submittedAt, filledAt);
      this.longInventory[symbol].push(lot);
    } else if (intent === "sell_to_close") {
      this.closeLongFIFO(symbol, qty, price, filledAt, order);
    } else if (intent === "sell_to_open") {
      const lot = this.createLot(order, "SHORT", qty, price, submittedAt, filledAt);
      this.shortInventory[symbol].push(lot);
    } else if (intent === "buy_to_close") {
      this.closeShortFIFO(symbol, qty, price, filledAt, order);
    } else {
      console.log(`      âš ï¸ UNKNOWN option intent: "${intent}" side="${side}"`);
    }
  }
  

  /**
   * Classify action for stocks (no position_intent)
   */
  classifyAction(order) {
    const intent = (order.position_intent || "").toLowerCase();
    const side = (order.side || "").toLowerCase();
    const symbol = order.symbol;

    // Use position_intent if available
    if (intent === "buy_to_open") return "OPEN_LONG";
    if (intent === "sell_to_close") return "CLOSE_LONG";
    if (intent === "sell_to_open") return "OPEN_SHORT";
    if (intent === "buy_to_close") return "CLOSE_SHORT";

    // Fallback: check existing inventory
    const hasLongPos =
      this.longInventory[symbol] && this.longInventory[symbol].length > 0;
    const hasShortPos =
      this.shortInventory[symbol] && this.shortInventory[symbol].length > 0;

    if (side === "buy") {
      return hasShortPos ? "CLOSE_SHORT" : "OPEN_LONG";
    }
    if (side === "sell") {
      return hasLongPos ? "CLOSE_LONG" : "OPEN_SHORT";
    }

    return "UNKNOWN";
  };


  /**
   * Create a lot for inventory
   */
  createLot(order, direction, qty, price, submittedAt, filledAt) {
    const entryType = this.pickOrderType(order);
    const entryLimit = this.pickLimitPrice(order);
    const entrySide = order.side;
    const entrySlip = this.calculateSlippage(entrySide, entryLimit, price);

    // Extract bracket details
    let [tpLimit, slStop, slLimit] = this.extractBracketInfo(order);
    console.log(`      ðŸŽ¯ Direct bracket extract: TP=${tpLimit} SL_STOP=${slStop} SL_LMT=${slLimit}`);

    // If entry execution doesn't carry bracket plan, inherit from stored plan (by id or parent)
    if (tpLimit == null && slStop == null && slLimit == null) {
      console.log(`      ðŸ" Looking up bracket plan by id=${order.id} parent=${order._parent_id}`);
      const plan = this.bracketPlanById.get(order.id) || this.bracketPlanById.get(order._parent_id);
      if (plan) {
        console.log(`      âœ… Found bracket plan:`, plan);
        tpLimit = plan.tp_limit ?? null;
        slStop  = plan.sl_stop ?? null;
        slLimit = plan.sl_limit ?? null;
      } else {
        console.log(`      âŒ No bracket plan found in bracketPlanById (size=${this.bracketPlanById.size})`);
        // Debug: show what's in the map
        if (this.bracketPlanById.size > 0) {
          console.log(`      ðŸ—ºï¸ Available bracket plans:`, Array.from(this.bracketPlanById.keys()));
        }
      }
    }
    
    const rr = this.calculateRR(direction, price, tpLimit, slStop);
    console.log(`      ðŸ'° Final bracket values: TP=${tpLimit} SL_STOP=${slStop} SL_LMT=${slLimit} RR=${rr}`);
    

    return {
      symbol: order.symbol,
      asset_class: order.asset_class,
      direction: direction,
      qty_open: qty, // CRITICAL: Track remaining open quantity
      entry_price: price,
      entry_submitted_at: submittedAt,
      entry_filled_at: filledAt,
      entry_order_type: entryType,
      entry_limit_price: entryLimit,
      entry_fill_price: price,
      entry_slippage: entrySlip,
      tp_limit: tpLimit,
      sl_stop: slStop,
      sl_limit: slLimit,
      rr: rr,
    };
  }

  /**
   * Close long positions using FIFO
   */
  closeLongFIFO(symbol, qtyToClose, exitPrice, exitFilledAt, exitOrder) {
    const inventory = this.longInventory[symbol] || [];
    console.log(
      `      ðŸ”½ closeLongFIFO: ${symbol} qty=${qtyToClose} price=${exitPrice} inventory_lots=${inventory.length}`
    );

    if (inventory.length === 0) {
      console.log(`      âš ï¸ No long inventory to close for ${symbol}!`);
      return;
    }

    let remaining = qtyToClose;

    const exitType = this.pickOrderType(exitOrder);
    const exitLimit = this.pickLimitPrice(exitOrder);
    const exitSide = exitOrder.side;
    const exitSlip = this.calculateSlippage(exitSide, exitLimit, exitPrice);

    let lotIndex = 0;
    while (remaining > 0 && inventory.length > 0) {
      const lot = inventory[0];
      const closeQty = Math.min(remaining, lot.qty_open);

      console.log(
        `      ðŸ“‰ Closing lot ${lotIndex}: closeQty=${closeQty} lot.qty_open=${lot.qty_open}`
      );

      // Calculate P&L for this lot
      // Options are quoted per-contract; realized P&L must be multiplied by 100.
      const isOption = lot.asset_class === "us_option";
      const mult = isOption ? 100 : 1;
      const pnl = (exitPrice - lot.entry_price) * closeQty * mult;

      // Calculate hold time
      const holdTime = this.calculateHoldTime(lot.entry_filled_at, exitFilledAt);

      // Create closed position record
      const closedPos = {
        symbol: symbol,
        asset_class: lot.asset_class,
        direction: "LONG",
        status: "closed",
        quantity: closeQty,
        qty_open: 0,

        entry_price: lot.entry_price,
        exit_price: exitPrice,
        pnl: pnl,

        entry_submitted_at: lot.entry_submitted_at,
        entry_filled_at: lot.entry_filled_at,
        exit_filled_at: exitFilledAt,

        entry_order_type: lot.entry_order_type,
        entry_limit_price: lot.entry_limit_price,
        entry_fill_price: lot.entry_fill_price,
        entry_slippage: lot.entry_slippage,

        exit_order_type: exitType,
        exit_limit_price: exitLimit,
        exit_fill_price: exitPrice,
        exit_slippage: exitSlip,

        tp_limit: lot.tp_limit,
        sl_stop: lot.sl_stop,
        sl_limit: lot.sl_limit,
        rr: lot.rr,

        hold_time_seconds: holdTime,
      };

      this.closedPositions.push(closedPos);

      // Update lot
      lot.qty_open -= closeQty;
      remaining -= closeQty;

      console.log(
        `      ðŸ“‰ After close: lot.qty_open=${lot.qty_open} remaining=${remaining}`
      );

      // Remove lot if fully closed
      if (lot.qty_open <= 0) {
        inventory.shift(); // Remove first element (FIFO)
        console.log(`      âœ… Lot fully closed, removed from inventory`);
      }

      lotIndex++;
    }

    if (remaining > 0) {
      console.log(
        `      âš ï¸ WARNING: ${remaining} shares could not be closed (no inventory)!`
      );
    }
  }

  /**
   * Close short positions using FIFO
   */
  closeShortFIFO(symbol, qtyToClose, exitPrice, exitFilledAt, exitOrder) {
    const inventory = this.shortInventory[symbol] || [];
    let remaining = qtyToClose;

    const exitType = this.pickOrderType(exitOrder);
    const exitLimit = this.pickLimitPrice(exitOrder);
    const exitSide = exitOrder.side;
    const exitSlip = this.calculateSlippage(exitSide, exitLimit, exitPrice);

    while (remaining > 0 && inventory.length > 0) {
      const lot = inventory[0];
      const closeQty = Math.min(remaining, lot.qty_open);

      // Calculate P&L for this lot (SHORT: entry - exit)
      const isOption = lot.asset_class === "us_option";
      const mult = isOption ? 100 : 1;
      const pnl = (lot.entry_price - exitPrice) * closeQty * mult;

      // Calculate hold time
      const holdTime = this.calculateHoldTime(lot.entry_filled_at, exitFilledAt);

      // Create closed position record
      const closedPos = {
        symbol: symbol,
        asset_class: lot.asset_class,
        direction: "SHORT",
        status: "closed",
        quantity: closeQty,
        qty_open: 0,

        entry_price: lot.entry_price,
        exit_price: exitPrice,
        pnl: pnl,

        entry_submitted_at: lot.entry_submitted_at,
        entry_filled_at: lot.entry_filled_at,
        exit_filled_at: exitFilledAt,

        entry_order_type: lot.entry_order_type,
        entry_limit_price: lot.entry_limit_price,
        entry_fill_price: lot.entry_fill_price,
        entry_slippage: lot.entry_slippage,

        exit_order_type: exitType,
        exit_limit_price: exitLimit,
        exit_fill_price: exitPrice,
        exit_slippage: exitSlip,

        tp_limit: lot.tp_limit,
        sl_stop: lot.sl_stop,
        sl_limit: lot.sl_limit,
        rr: lot.rr,

        hold_time_seconds: holdTime,
      };

      this.closedPositions.push(closedPos);

      // Update lot
      lot.qty_open -= closeQty;
      remaining -= closeQty;

      // Remove lot if fully closed
      if (lot.qty_open <= 0) {
        inventory.shift(); // Remove first element (FIFO)
      }
    }
  }

  /**
   * Get active and closed positions
   */
  getTableView() {
    // Collect active positions from all inventories
    const activePositions = [];

    console.log("ðŸ“¦ Collecting active positions...");

    // Long positions
    for (const [symbol, lots] of Object.entries(this.longInventory)) {
      for (const lot of lots) {
        if (lot.qty_open > 0) {
          console.log(
            `   ðŸ”µ ACTIVE LONG: ${symbol} qty_open=${lot.qty_open} entry=${lot.entry_price}`
          );
          activePositions.push({
            ...lot,
            status: "active",
            quantity: lot.qty_open,
          });
        }
      }
    }

    // Short positions
    for (const [symbol, lots] of Object.entries(this.shortInventory)) {
      for (const lot of lots) {
        if (lot.qty_open > 0) {
          console.log(
            `   ðŸ”´ ACTIVE SHORT: ${symbol} qty_open=${lot.qty_open} entry=${lot.entry_price}`
          );
          activePositions.push({
            ...lot,
            status: "active",
            quantity: lot.qty_open,
          });
        }
      }
    }

    console.log("ðŸ“¦ Total active positions:", activePositions.length);
    console.log("ðŸ“¦ Total closed positions:", this.closedPositions.length);

    // Combine active and closed
    const allPositions = [...activePositions, ...this.closedPositions];

    // Separate stocks and options
    const stocks = allPositions.filter((p) => p.asset_class === "us_equity");
    const options = allPositions.filter((p) => p.asset_class === "us_option");

    // Format for table display
    const formattedStocks = stocks.map((p) => this.formatPositionForTable(p));
    const formattedOptions = options.map((p) => this.formatPositionForTable(p));

    // Calculate summary
    const summary = this.calculateSummary(stocks, options);

    console.log("ðŸ“Š Table view generated:");
    console.log(
      "   - Stocks:",
      formattedStocks.length,
      "(active:",
      stocks.filter((p) => p.status === "active").length,
      ")"
    );
    console.log(
      "   - Options:",
      formattedOptions.length,
      "(active:",
      options.filter((p) => p.status === "active").length,
      ")"
    );

    return {
      stocks: formattedStocks,
      options: formattedOptions,
      summary: summary,
    };
  }

  /**
   * Format position for table display
   */
  formatPositionForTable(position) {
    const opt = position.asset_class === "us_option" ? this.parseOptionSymbol(position.symbol) : null;
    return {
      symbol: position.symbol,
      status: position.status,
      direction: position.direction,
      quantity: position.quantity || position.qty_open || 0,

      entry_submitted_at: this.formatTimestamp(position.entry_submitted_at),
      entry_filled_at: this.formatTimeOnly(position.entry_filled_at),
      entry_order_type: position.entry_order_type || "",
      entry_limit_price: position.entry_limit_price,
      entry_fill_price: position.entry_fill_price,
      entry_slippage: position.entry_slippage,

      exit_filled_at: this.formatTimeOnly(position.exit_filled_at),
      exit_order_type: position.exit_order_type || "",
      exit_limit_price: position.exit_limit_price,
      exit_fill_price: position.exit_fill_price,
      exit_slippage: position.exit_slippage,

      avg_entry_price: (position.entry_price || 0).toFixed(2),
      exit_price: position.exit_price ? position.exit_price.toFixed(2) : null,

      tp_limit: position.tp_limit,
      sl_stop: position.sl_stop,
      sl_limit: position.sl_limit,
      rr: position.rr,

      // UI expects total_pnl / total_pnl_num (and optional unrealized_pnl for active)
      total_pnl_num: position.pnl || 0,
      total_pnl: (position.pnl ?? 0).toFixed(2),

      unrealized_pnl_num: 0,
      unrealized_pnl: "0.00",


      hold_time: this.formatHoldTime(position.hold_time_seconds),
      hold_time_seconds: position.hold_time_seconds,
      first_entry_time: position.entry_submitted_at || position.entry_filled_at || null,

      option_type: opt?.option_type || null,
      strike: opt?.strike || null,
      expiration: opt?.expiration || null,
    };
  }

  /**
   * Calculate summary statistics
   */
  calculateSummary(stocks, options) {
    const activeStocks = stocks.filter((p) => p.status === "active").length;
    const closedStocks = stocks.filter((p) => p.status === "closed").length;
    const activeOptions = options.filter((p) => p.status === "active").length;
    const closedOptions = options.filter((p) => p.status === "closed").length;

    const stockPnL = stocks.reduce((sum, p) => sum + (p.pnl || 0), 0);
    const optionPnL = options.reduce((sum, p) => sum + (p.pnl || 0), 0);
    const totalPnL = stockPnL + optionPnL;

    return {
      total_pnl: totalPnL.toFixed(2),
      stock_pnl: stockPnL.toFixed(2),
      option_pnl: optionPnL.toFixed(2),

      active_stocks: activeStocks,
      active_options: activeOptions,
      closed_stocks: closedStocks,
      closed_options: closedOptions,

      total_active: activeStocks + activeOptions,
      total_closed: closedStocks + closedOptions,

      total_pnl_num: totalPnL,
      stock_pnl_num: stockPnL,
      option_pnl_num: optionPnL,
    };
  }

  // ========== HELPER METHODS ==========

  pickOrderType(order) {
    return (order.type || order.order_type || "").toLowerCase();
  }

  pickLimitPrice(order) {
    const limit = order.limit_price;
    return limit !== null && limit !== undefined ? parseFloat(limit) : null;
  }

  calculateSlippage(side, limitPrice, fillPrice) {
    if (
      limitPrice === null ||
      fillPrice === null ||
      limitPrice === undefined ||
      fillPrice === undefined
    ) {
      return null;
    }

    side = (side || "").toLowerCase();
    if (side === "buy") {
      return fillPrice - limitPrice; // Positive = worse fill
    }
    if (side === "sell") {
      return limitPrice - fillPrice; // Positive = worse fill
    }
    return null;
  }

  extractBracketInfo(order) {
    // ðŸ"¥ DEBUG: Dump ENTIRE order for first bracket
    if (order.order_class === "bracket" && !this._debuggedBracket) {
      this._debuggedBracket = true;
      console.log(" FULL BRACKET ORDER:", JSON.stringify(order, null, 2));
    }
    console.log(`         ðŸ" extractBracketInfo for order ${order.id}:`, {
      has_take_profit: !!order.take_profit,
      has_stop_loss: !!order.stop_loss,
      has_legs: !!order.legs,
      legs_count: order.legs?.length || 0
    });

    // Check top-level
    const tp = order.take_profit || {};
    const sl = order.stop_loss || {};

    // ✅ FIXED: Match PrepareOrder.js - check nested objects THEN top-level fields
    let tpLimit =
      (tp.limit_price !== null && tp.limit_price !== undefined)
        ? parseFloat(tp.limit_price)
        : (order.take_profit_price !== null && order.take_profit_price !== undefined)
          ? parseFloat(order.take_profit_price)
          : null;
          
    let slStop =
      (sl.stop_price !== null && sl.stop_price !== undefined)
        ? parseFloat(sl.stop_price)
        : (order.stop_loss_price !== null && order.stop_loss_price !== undefined)
          ? parseFloat(order.stop_loss_price)
          : null;
          
    let slLimit =
      (sl.limit_price !== null && sl.limit_price !== undefined)
        ? parseFloat(sl.limit_price)
        : null;

    console.log(`         📊 Extracted: TP=${tpLimit}, SL_STOP=${slStop}, SL_LMT=${slLimit}`);

    if (tpLimit !== null || slStop !== null || slLimit !== null) {
      return [tpLimit, slStop, slLimit];
    }

    // Check legs
    const legs = order.legs || [];
    if (!Array.isArray(legs)) {
      return [null, null, null];
    }

    let tpLeg = null;
    let slLeg = null;

    for (const leg of legs) {
      if (!leg || typeof leg !== "object") continue;

      const legType = (leg.type || leg.order_type || "").toLowerCase();
      const stopPx = leg.stop_price;

      // TP is limit with no stop_price
      if (legType === "limit" && (!stopPx || stopPx === "" || parseFloat(stopPx) === 0)) {
        tpLeg = leg;
      }

      // SL is stop/stop_limit with stop_price
      if (
        (legType === "stop" || legType === "stop_limit") &&
        stopPx &&
        parseFloat(stopPx) !== 0
      ) {
        slLeg = leg;
      }
    }

    tpLimit = tpLeg ? parseFloat(tpLeg.limit_price || 0) || null : null;
    slStop = slLeg ? parseFloat(slLeg.stop_price || 0) || null : null;
    slLimit = slLeg ? parseFloat(slLeg.limit_price || 0) || null : null;

    return [tpLimit, slStop, slLimit];
  }

  calculateRR(direction, entry, tp, slStop) {
    if (!entry || !tp || !slStop) return null;

    try {
      entry = parseFloat(entry);
      tp = parseFloat(tp);
      slStop = parseFloat(slStop);
    } catch (e) {
      return null;
    }

    let reward, risk;
    if (direction === "LONG") {
      reward = tp - entry;
      risk = entry - slStop;
    } else {
      reward = entry - tp;
      risk = slStop - entry;
    }

    if (risk <= 0 || reward <= 0) return null;
    return parseFloat((reward / risk).toFixed(2));
  }

  calculateHoldTime(entryTime, exitTime) {
    if (!entryTime || !exitTime) return null;

    try {
      const entry = new Date(entryTime);
      const exit = new Date(exitTime);
      return (exit - entry) / 1000; // seconds
    } catch (e) {
      return null;
    }
  }

  formatHoldTime(seconds) {
    if (!seconds || seconds === null) return "";

    const s = Math.floor(seconds);
    const m = Math.floor(s / 60);
    const h = Math.floor(m / 60);

    const secs = s % 60;
    const mins = m % 60;

    if (h > 0) {
      return `${h}h${mins.toString().padStart(2, "0")}m${secs
        .toString()
        .padStart(2, "0")}s`;
    }
    return `${mins}m${secs.toString().padStart(2, "0")}s`;
  }

  formatTimestamp(timestamp) {
    if (!timestamp) return "";

    const date = new Date(timestamp);
    return date.toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  }

  formatTimeOnly(timestamp) {
    if (!timestamp) return "";

    const date = new Date(timestamp);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  }

  reset() {
    this.longInventory = {};
    this.shortInventory = {};
    this.closedPositions = [];
    this.processedOrderIds.clear();
  }

  parseOptionSymbol(symbol) {
    // Example: SPY260206P00675000
    const m = String(symbol || "").match(/^([A-Z]{1,6})(\d{2})(\d{2})(\d{2})([CP])(\d{8})$/);
    if (!m) return null;
  
    const [, underlying, yy, mm, dd, cp, strike8] = m;
    const year = 2000 + parseInt(yy, 10);
    const month = parseInt(mm, 10);
    const day = parseInt(dd, 10);
    const expiration = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const option_type = cp === "C" ? "call" : "put";
    const strike = (parseInt(strike8, 10) / 1000).toFixed(0); // 00675000 => 675
  
    return { underlying, expiration, option_type, strike };
  }
}


export default OrderLedgerBuilder;